import { forwardRef, useId, useRef, useState } from 'react';

/**
 * Tablist + tabpanels (spec §7.1). Arrow keys move focus AND activate the
 * target tab (automatic activation), with Home/End jumping to the first/last
 * enabled tab. Works controlled (`value` + `onChange`) or uncontrolled
 * (`defaultValue`).
 *
 * @param {object} props
 * @param {{id: string, label: import('react').ReactNode, panel: import('react').ReactNode, disabled?: boolean}[]} props.items
 * @param {string} [props.value] - controlled active tab id.
 * @param {string} [props.defaultValue] - uncontrolled initial tab id.
 * @param {(id: string) => void} [props.onChange]
 * @param {string} [props.ariaLabel] - accessible name for the tablist itself
 *   (only needed when the tabs aren't already introduced by visible heading text).
 * @param {'horizontal'|'vertical'} [props.orientation='horizontal'] - per the
 *   ARIA APG tabs pattern, only the axis matching the orientation moves focus
 *   (Right/Left for horizontal, Down/Up for vertical) — the other axis is left
 *   free for the browser/AT to use. Home/End always jump to first/last.
 * @param {string} [props.className] - applied to the outer wrapper (the
 *   forwarded ref's root); the tablist itself always carries `.etch-tabs`.
 */
export const Tabs = forwardRef(function Tabs(
  { items, value, defaultValue, onChange, ariaLabel, orientation = 'horizontal', className = '', ...rest },
  ref
) {
  const baseId = useId();
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? items.find((item) => !item.disabled)?.id);
  const activeId = isControlled ? value : internalValue;
  const tabRefs = useRef({});

  const enabledItems = items.filter((item) => !item.disabled);

  const selectTab = (id) => {
    if (!isControlled) setInternalValue(id);
    onChange?.(id);
  };

  const focusAndSelect = (id) => {
    selectTab(id);
    tabRefs.current[id]?.focus();
  };

  const handleKeyDown = (event) => {
    if (enabledItems.length === 0) return;
    const currentIndex = enabledItems.findIndex((item) => item.id === activeId);
    const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';

    switch (event.key) {
      case nextKey:
        event.preventDefault();
        focusAndSelect(enabledItems[(currentIndex + 1) % enabledItems.length].id);
        break;
      case prevKey:
        event.preventDefault();
        focusAndSelect(enabledItems[(currentIndex - 1 + enabledItems.length) % enabledItems.length].id);
        break;
      case 'Home':
        event.preventDefault();
        focusAndSelect(enabledItems[0].id);
        break;
      case 'End':
        event.preventDefault();
        focusAndSelect(enabledItems[enabledItems.length - 1].id);
        break;
      default:
        break;
    }
  };

  const tabId = (id) => `${baseId}-tab-${id}`;
  const panelId = (id) => `${baseId}-panel-${id}`;

  return (
    <div ref={ref} className={className} {...rest}>
      <div
        className="etch-tabs"
        role="tablist"
        aria-label={ariaLabel}
        aria-orientation={orientation}
        onKeyDown={handleKeyDown}
      >
        {items.map((item) => {
          const selected = item.id === activeId;
          return (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[item.id] = el;
              }}
              id={tabId(item.id)}
              type="button"
              role="tab"
              className="etch-tab"
              aria-selected={selected}
              aria-controls={panelId(item.id)}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => selectTab(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          id={panelId(item.id)}
          role="tabpanel"
          aria-labelledby={tabId(item.id)}
          hidden={item.id !== activeId}
          tabIndex={0}
        >
          {item.panel}
        </div>
      ))}
    </div>
  );
});

export default Tabs;
